/**
 * Client-side logging and analytics for web.
 *
 * Two concerns:
 *  1. Firebase Analytics events (user journey tracking)
 *  2. Error/warning logs sent to logClientError Cloud Function
 *
 * All logging includes:
 *  - requestId (correlation ID, generated per user action)
 *  - userId (from AuthContext)
 *  - service: 'web'
 *  - screen (current page/route)
 */
import { logEvent as firebaseLogEvent, getAnalytics, isSupported } from 'firebase/analytics';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../services/firebase';

type ClientSeverity = 'error' | 'warn';

interface LogPayload {
  severity: ClientSeverity;
  message: string;
  service: 'web';
  screen?: string;
  requestId?: string;
  userId?: string | null;
  meta?: Record<string, unknown>;
}

let analyticsInstance: ReturnType<typeof getAnalytics> | null = null;
let analyticsSupported = false;

// Initialize analytics lazily
async function getAnalyticsInstance() {
  if (analyticsInstance) return analyticsInstance;
  analyticsSupported = await isSupported();
  if (analyticsSupported) {
    // Dynamic import to avoid loading analytics in unsupported environments
    const { firebaseApp } = await import('../services/firebase');
    analyticsInstance = getAnalytics(firebaseApp);
  }
  return analyticsInstance;
}

// Pre-warm analytics on module load (non-blocking)
getAnalyticsInstance().catch(() => {
  // Ignore errors — analytics may be blocked by browser
});

/**
 * Generate a short correlation ID for linking client → server logs.
 * Call once per user action (search, verification, etc.) and pass to both
 * analytics events and server calls.
 */
export function generateRequestId(): string {
  return crypto.randomUUID().slice(0, 8);
}

/**
 * Get current screen name from location.
 */
function getCurrentScreen(): string {
  const path = window.location.pathname;
  if (path === '/' || path === '') return 'home';
  if (path.startsWith('/parking/')) return 'parking_detail';
  if (path.startsWith('/verify/')) return 'verification';
  if (path === '/garage') return 'garage';
  if (path === '/profile') return 'profile';
  if (path === '/signin') return 'signin';
  return path.slice(1).replace(/\//g, '_') || 'unknown';
}

// Shared state for userId (set by AuthContext)
let currentUserId: string | null = null;

/**
 * Set the current user ID. Call from AuthContext when auth state changes.
 */
export function setLoggerUserId(userId: string | null) {
  currentUserId = userId;
}

/**
 * Log an error or warning to Cloud Logging via the logClientError function.
 * Fire-and-forget — doesn't block on the network call.
 */
export function logToServer(
  severity: ClientSeverity,
  message: string,
  meta?: Record<string, unknown>,
  requestId?: string
) {
  const payload: LogPayload = {
    severity,
    message,
    service: 'web',
    screen: getCurrentScreen(),
    requestId,
    userId: currentUserId,
    meta,
  };

  // Fire and forget — don't await
  const callable = httpsCallable(functions, 'logClientError');
  callable(payload).catch(() => {
    // Silently ignore logging failures
  });
}

/**
 * Log an error to Cloud Logging.
 */
export function logError(message: string, error?: unknown, meta?: Record<string, unknown>, requestId?: string) {
  const errorMeta: Record<string, unknown> = { ...meta };
  if (error instanceof Error) {
    errorMeta.errorMessage = error.message;
    errorMeta.errorStack = error.stack?.slice(0, 500);
  } else if (error !== undefined) {
    errorMeta.errorMessage = String(error);
  }
  logToServer('error', message, errorMeta, requestId);
}

/**
 * Log a warning to Cloud Logging.
 */
export function logWarn(message: string, meta?: Record<string, unknown>, requestId?: string) {
  logToServer('warn', message, meta, requestId);
}

// ============================================================================
// Firebase Analytics Events
// ============================================================================

type AnalyticsParams = Record<string, string | number | boolean | null | undefined>;

async function logAnalyticsEvent(eventName: string, params?: AnalyticsParams) {
  if (!analyticsSupported) return;
  const analytics = await getAnalyticsInstance();
  if (!analytics) return;

  // Add common params
  const enrichedParams: AnalyticsParams = {
    ...params,
    user_id: currentUserId ?? undefined,
    screen: getCurrentScreen(),
  };

  firebaseLogEvent(analytics, eventName, enrichedParams);
}

// Search flow events
export const analytics = {
  searchStarted: (params: {
    query?: string;
    lat?: number;
    lng?: number;
    placeType?: string;
    radiusKm?: number;
    requestId: string;
  }) => logAnalyticsEvent('search_started', params),

  searchCompleted: (params: {
    requestId: string;
    status: 'ready' | 'failed';
    parkingCount?: number;
    durationMs: number;
  }) => logAnalyticsEvent('search_completed', params),

  // Parking events
  parkingViewed: (params: { parkingId: string; distanceKm?: number }) =>
    logAnalyticsEvent('parking_viewed', params),

  parkingDetailOpened: (params: { parkingId: string }) =>
    logAnalyticsEvent('parking_detail_opened', params),

  // Verification events
  verificationStarted: (params: { parkingId: string; method: 'manual' | 'photo'; requestId: string }) =>
    logAnalyticsEvent('verification_started', params),

  verificationCompleted: (params: {
    parkingId: string;
    method: 'manual' | 'photo';
    status: 'accepted' | 'pending_review';
    requestId: string;
  }) => logAnalyticsEvent('verification_completed', params),

  // Location events
  locationRequested: () => logAnalyticsEvent('location_requested'),

  locationGranted: (params: { lat: number; lng: number }) =>
    logAnalyticsEvent('location_granted', params),

  locationDenied: (params: { errorCode?: string }) =>
    logAnalyticsEvent('location_denied', params),

  // Acquisition tracking
  userAcquired: (params: {
    source: string;
    medium: string;
    campaign?: string | null;
    referrer?: string | null;
  }) => logAnalyticsEvent('user_acquired', params),

  // Car events
  carAdded: (params: {
    carType: string;
    heightM: number;
    label?: string;
  }) => logAnalyticsEvent('car_added', params),

  carRemoved: (params: {
    carType: string;
  }) => logAnalyticsEvent('car_removed', params),
};
